import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, ApiError, type ApiInvitee } from "../lib/api";

type LoadState =
  | { kind: "loading" }
  | { kind: "ready" }
  | { kind: "error"; message: string };

type Props = { projectId: string };

// Owner-only invitees panel rendered in the project sidebar. Same visual
// language as the Pages panel above it (uppercase eyebrow, vertical list,
// inline composer for adds). Each row exposes resend + revoke. Revoked
// rows fade out but remain in the list so the owner has an audit view.

export function InviteesPanel({ projectId }: Props) {
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });
  const [invitees, setInvitees] = useState<ApiInvitee[]>([]);
  const [adding, setAdding] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoad({ kind: "loading" });
    try {
      const res = await api.listInvitees(projectId);
      setInvitees(res.invitees);
      setLoad({ kind: "ready" });
    } catch (err) {
      setLoad({ kind: "error", message: (err as Error).message ?? "failed" });
    }
  }, [projectId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  function flash(message: string) {
    setToast(message);
    setTimeout(() => setToast(null), 2500);
  }

  async function onAdd(input: { name: string; email: string }) {
    try {
      const res = await api.addInvitee(projectId, input);
      // Insert at the top — server orders by created_at desc.
      setInvitees((prev) => {
        const without = prev.filter((p) => p.id !== res.invitee.id);
        return [res.invitee, ...without];
      });
      flash("Invite link sent");
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        flash("Check name and email");
      } else {
        flash("Couldn’t add invitee");
      }
      throw err;
    }
  }

  async function onResend(inviteeId: string) {
    try {
      await api.resendInvitee(projectId, inviteeId);
      flash("Sent fresh link");
    } catch {
      flash("Couldn’t resend");
    }
  }

  async function onRevoke(invitee: ApiInvitee) {
    if (
      !confirm(
        `Revoke access for ${invitee.email}? They'll lose access to this project but stay signed in to any other projects they're invited to.`,
      )
    )
      return;
    try {
      await api.revokeInvitee(projectId, invitee.id);
      setInvitees((prev) =>
        prev.map((p) =>
          p.id === invitee.id ? { ...p, status: "revoked" as const } : p,
        ),
      );
      flash("Revoked");
    } catch {
      flash("Couldn’t revoke");
    }
  }

  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">
        Invitees
      </div>

      {load.kind === "loading" ? (
        <p className="mt-2 text-xs text-neutral-400">Loading…</p>
      ) : load.kind === "error" ? (
        <p className="mt-2 text-xs text-red-600">Couldn’t load invitees.</p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {invitees.map((inv) => (
            <li
              key={inv.id}
              className="group rounded-md border border-neutral-200 bg-white p-2"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p
                    className={
                      "truncate text-xs font-medium " +
                      (inv.status === "revoked"
                        ? "text-neutral-400 line-through"
                        : "text-neutral-900")
                    }
                  >
                    {inv.name}
                  </p>
                  <p className="truncate text-[11px] text-neutral-500">{inv.email}</p>
                  <p className="mt-0.5 text-[10px] text-neutral-400">
                    {inv.status === "revoked"
                      ? "Revoked"
                      : inv.lastSeenAt
                        ? `Last seen ${formatRelative(inv.lastSeenAt)}`
                        : "Not yet signed in"}
                  </p>
                </div>
                {inv.status === "active" ? (
                  <div className="invisible flex shrink-0 flex-col gap-0.5 text-[10px] text-neutral-500 group-hover:visible">
                    <button
                      type="button"
                      onClick={() => void onResend(inv.id)}
                      className="hover:text-neutral-900"
                    >
                      Resend
                    </button>
                    <button
                      type="button"
                      onClick={() => void onRevoke(inv)}
                      className="hover:text-red-600"
                    >
                      Revoke
                    </button>
                  </div>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      {adding ? (
        <AddInviteeForm
          onSubmit={async (input) => {
            await onAdd(input);
            setAdding(false);
          }}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="mt-2 flex w-full items-center gap-1 rounded-md px-2 py-1.5 text-xs text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
        >
          <span aria-hidden>+</span>
          <span>Add invitee</span>
        </button>
      )}

      {toast ? (
        <p className="mt-2 text-[11px] text-neutral-600">{toast}</p>
      ) : null}
    </div>
  );
}

function AddInviteeForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (input: { name: string; email: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await onSubmit({ name: name.trim(), email: email.trim() });
    } catch {
      // surface handled upstream via flash
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mt-2 space-y-1 rounded-md bg-neutral-100 p-2">
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Full name"
        required
        maxLength={80}
        className="w-full rounded border-0 bg-white px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-neutral-900"
      />
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email"
        required
        maxLength={320}
        className="w-full rounded border-0 bg-white px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-neutral-900"
      />
      <div className="flex justify-end gap-1">
        <button
          type="button"
          onClick={onCancel}
          className="px-2 text-[11px] text-neutral-500 hover:text-neutral-900"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy}
          className="rounded bg-neutral-900 px-2 py-0.5 text-[11px] text-white hover:bg-neutral-700 disabled:opacity-50"
        >
          {busy ? "Sending…" : "Send invite"}
        </button>
      </div>
    </form>
  );
}

function formatRelative(ts: number): string {
  const now = Date.now();
  const diff = now - ts;
  if (diff < 60_000) return "just now";
  const min = Math.floor(diff / 60_000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
}
